import { ApiService } from "./AbstractApiService";
import qs from "qs";
import { IPageModel } from "@/interfaces/iPageModel";
import API from "./handlers/base.service";
import {
  ICreateUserModel,
  IRolePermissionModel,
  IUser,
  IUserClone,
  IUserForm,
  IUserFormCreate,
  IUserPermission,
  IUserRoleModel,
  IUserSite,
  IUserSiteCreate,
  IUserActivity,
  IContactModel,
} from "@/interfaces/iUser";
import { ILocationActivity } from "@/interfaces/iLocation";
import { IFormActivity } from "@/interfaces/iForm";
import { ISiteActivity, IDeviceActivity } from "@/interfaces/iSite";
import { RESET_PASSWORD_CONFIG } from "@/views/user/dataTableOptions";
import { ICurrentUser, IUserPhoneModel } from "@/interfaces/iCurrentUser";
import { IRole } from "@/interfaces/iRole";

export class UserAPI extends ApiService {
  public static getInstance() {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  private static instance: UserAPI | null;
  constructor() {
    super("security");
  }

  public async getUserList(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IUser>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("userAccounts", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getUserActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IUserActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits?type=300,1400,1100,2000,2300", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getRoleActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IUserActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getFormActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IFormActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits?type=100,700,2700,1600,2000,1900,1800", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getSiteActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<ISiteActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits?type=200,1100,1600,1500,2100,1700", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getDeviceActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<IDeviceActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits?type=400,1500", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async getLocationActivity(
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<ILocationActivity>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return this.get("audits?type=2600,1700,1500", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public transformDateRangeParams(
    params: Record<string, string | number>
  ): Record<string, string | number> {
    if (
      params &&
      params["startDate"] &&
      `${params["startDate"]}`.split(",").length > 1
    ) {
      const dates = `${params["startDate"]}`.split(",");
      params["startDate"] = dates[0];
      const endDate = new Date(dates[1]);
      endDate.setHours(23, 59, 59);

      // Format the end date as a string without converting to UTC
      const endDateString = `${endDate.getFullYear()}-${String(
        endDate.getMonth() + 1
      ).padStart(2, "0")}-${String(endDate.getDate()).padStart(
        2,
        "0"
      )}T${String(endDate.getHours()).padStart(2, "0")}:${String(
        endDate.getMinutes()
      ).padStart(2, "0")}:${String(endDate.getSeconds()).padStart(
        2,
        "0"
      )}+05:00`;

      params["endDate"] = endDateString;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ...rest } = params;
    return rest;
  }
  public async getUserRoles(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserRoleModel>> {
    return this.get("userRoles", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getUserRoleWithSites(
    id,
    queryParams?: Record<string, string | number>
  ): Promise<IPageModel<any>> {
    const params = this.transformDateRangeParams(queryParams ?? {});
    return API.get(`site/userSites/userRoles?userIds=${id}`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async submitCloneUser(data): Promise<any> {
    return this.post("userAccounts/global", data).then(
      (response) => response.data
    );
  }

  public async submitCloneRole(data): Promise<any> {
    return this.post("roles/clone", data).then((response) => response.data);
  }

  public async createUserRoleWithSites(data): Promise<any> {
    return API.post(`site/userSites/userPermissions`, data).then(
      (response) => response.data
    );
  }

  public async getRolePermissions(): Promise<any> {
    return this.get("rolePermissions").then((response) => response.data);
  }

  public async saveRolePermissions(data): Promise<any> {
    return this.post("rolePermissions", data).then((response) => response.data);
  }

  public async deleteRolePermission(id): Promise<any> {
    return this.delete(`rolePermissions/${id}`).then(
      (response) => response.data
    );
  }

  public async deleteUserSites(data): Promise<any> {
    return API.delete(`site/userSites/delete`, { data }).then(
      (response) => response.data
    );
  }

  public async getRolePermissionsPageable(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IRolePermissionModel>> {
    return this.get("rolePermissions/paged", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getAllPermissions(): Promise<any> {
    return this.get("permissions").then((response) => response.data);
  }

  public async getActiveRoles(id: number): Promise<any> {
    return this.get(`userRoles/${id}/userAccount`).then(
      (response) => response.data
    );
  }

  public async getPermissions(
    params?: Record<string, string | number>
  ): Promise<any> {
    return this.get("permissions/page", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getPermissionGroups(): Promise<any> {
    return this.get("permissionGroups").then((response) => response.data);
  }

  public async getUserPermissions(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserPermission>> {
    return this.get("userPermissions", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async createUserPermissions(data: any) {
    return this.post("userPermissions", data).then((response) => response.data);
  }

  public async deleteUserPermissions(id): Promise<any> {
    return this.delete(`userPermissions/${id}`).then(
      (response) => response.data
    );
  }

  public async bulkDeleteUserPermissions(data): Promise<any> {
    return API.delete(`security/userPermissions`, { data }).then(
      (response) => response.data
    );
  }

  public async assigbUserPermissions(data: any) {
    return this.post("userPermissions/assign", data).then(
      (response) => response.data
    );
  }

  public async getUserPermissionsByIds(
    userIds,
    siteIds,
    userRoleIds,
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserPermission>> {
    return this.get(
      `rolePermissions/group?userIds=${userIds}&userRoleIds=${userRoleIds}&siteIds=${siteIds}`,
      {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
      }
    ).then((response) => response.data);
  }

  public async getUserById(id: number): Promise<IUser> {
    return this.get(`userAccounts/${id}`).then((response) => response.data);
  }

  public async updateUserFileId(id: number, fileId: any) {
    return this.patch(`userAccounts/${id}`, fileId).then(
      (response) => response.data
    );
  }

  public async getRoleById(id: number): Promise<IRole> {
    return this.get(`roles/${id}`).then((response) => response.data);
  }

  public async getUserPermissionsById(
    userId,
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserPermission>> {
    return this.get(`userPermissions/group?userIds=${userId}`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getUserPermissionsByUserAndSiteIds(
    userId,
    siteIds,
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserPermission>> {
    return this.get(
      `userPermissions/sitePermissions?userIds=${userId}&siteId=${siteIds}`,
      {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
      }
    ).then((response) => response.data);
  }

  public async getUserSites(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserSite>> {
    return API.get("site/userSites", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getUserForms(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserForm>> {
    return API.get("forms/formUsers", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async cloneUser(params: IUserClone) {
    return this.post("userAccounts/clone", params).then(
      (response) => response.data
    );
  }
  public async addSite(payload: IUserSiteCreate) {
    return API.post("site/userSites", payload).then(
      (response) => response.data
    );
  }
  public async addUserForms(payload: IUserFormCreate) {
    return API.post("forms/formUsers/multiple", payload).then(
      (response) => response.data
    );
  }
  public async resetPassword(userId: number) {
    return this.post(
      `userAccounts/resetPassword?userAccountId=${userId}`,
      RESET_PASSWORD_CONFIG
    ).then((response) => response.data);
  }
  public async createUser(data: ICreateUserModel) {
    return this.post(`userAccounts`, data).then((response) => response.data);
  }
  public async updateUser(data: ICreateUserModel) {
    return this.put(`userAccounts/${data?.webId}`, data).then(
      (response) => response.data
    );
  }
  public async updateAccount(data: IUser, id?: number) {
    return this.put(`userAccounts/${id}`, data).then(
      (response) => response.data
    );
  }
  public async addContactDetail(data: IContactModel) {
    return this.post(`contactDetails`, data).then((response) => response.data);
  }
  public async createRole(data): Promise<any> {
    return this.post("roles", data).then((response) => response.data);
  }
  public async updateRole(data: any, webId: any) {
    return this.patch(`roles/${webId}`, data).then((response) => response.data);
  }
  public bulkDeleteRole(entity: string, params: string[]) {
    return this.service
      .delete(`/security/${entity}`, { data: params })
      .then((response) => response.data);
  }

  public async getRoles(
    params?: Record<string, string | number>
  ): Promise<IPageModel<IUserForm>> {
    return this.get("roles", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async getUser(params: number): Promise<IUser> {
    return this.get("userAccounts/" + params).then((response) => response.data);
  }
  public async getClientDetails(clientId: number | undefined): Promise<any> {
    return this.get(`clients/${clientId}`).then((response) => response.data);
  }
  public async updateClientDetails(
    data: Record<string, string | number>
  ): Promise<any> {
    return this.put("clients", data).then((response) => response.data);
  }

  public async getClientAddress(addressId: number): Promise<any> {
    return this.get(`address/${addressId}`).then((response) => response.data);
  }
  public async updatePhoneModel(userId: number, data: IUserPhoneModel[]) {
    return this.put(
      `userAccounts/userAccount/${userId}/updatePhone`,
      data
    ).then((response) => response.data);
  }
  public async getAllRoles(
    params?: Record<string, string | number>
  ): Promise<any> {
    return this.get("roles/all", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async getRolesWithUserCount(
    params?: Record<string, string | number>
  ): Promise<any> {
    return this.get("userRoles/count", {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }
  public async getUsersOnRoleId(
    id: string,
    payload?: Record<string, string | number>
  ): Promise<Array<ICurrentUser>> {
    return this.get(`userAccounts/role/${id}?${payload}`, {
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "comma" }),
    }).then((response) => response.data);
  }

  public async updateUserBulkStatus(status: string, webId: any) {
    return this.patch(
      `userAccounts/bulkStatusUpdate?status=${status}`,
      webId
    ).then((response) => response.data);
  }
  public async updateRoleModuel(params?: Record<string, string | number>) {
    return this.put(`rolePermissions/togglePermissions`, params).then(
      (response) => response.data
    );
  }
  public async updateSpecificRoleModuel(
    params?: Record<string, string | number>
  ) {
    return this.put(`rolePermissions/togglePermissions/update`, params).then(
      (response) => response.data
    );
  }
  public async checkedModuleToggleButton(id: number): Promise<any> {
    return this.get(`rolePermissions/togglePermissions?roleId=${id}`).then(
      (response) => response.data
    );
  }
}
export async function getMethod<T extends string>(
  url: string
): Promise<Array<Record<any, any>>> {
  return API.get(url).then((res) => {
    return res.data;
  });
}
